import DescriptionIcon from '@mui/icons-material/Description';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import type { FC, MouseEvent } from 'react';
import { useState } from 'react';

import { hideScroll } from '@/app/components/Provider/ThemeProvider/css';
import { useExportFile } from '@/hooks/useExportFile';

export default function DownloadFilePopover() {
  const [open, setOpen] = useState<HTMLButtonElement | null>(null);
  const [preparingList, setPreparingList] = useState<string[]>([]);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handlePreparedFile = (variables: Record<string, string | number>) => {
    const filename = variables.filename;

    const filtered = [...preparingList].filter((item) => item !== filename);
    if (!filtered.length) {
      setOpen(null);
    }
    setPreparingList(filtered);
  };

  const exportLeadsMutation = useExportFile({
    type: 'export_leads',
    addPreparingFile: (name) => setPreparingList((prev) => [...prev, name]),
    onSuccess: (_, variables) => handlePreparedFile(variables),
  });

  const exportAccountsMutation = useExportFile({
    type: 'export_accounts',
    addPreparingFile: (name) => setPreparingList((prev) => [...prev, name]),
    onSuccess: (_, variables) => handlePreparedFile(variables),
  });

  const exportClassesMutation = useExportFile({
    type: 'export_classes',
    addPreparingFile: (name) => setPreparingList((prev) => [...prev, name]),
    onSuccess: (_, variables) => handlePreparedFile(variables),
  });

  const exportContractsMutation = useExportFile({
    type: 'export_contracts',
    addPreparingFile: (name) => setPreparingList((prev) => [...prev, name]),
    onSuccess: (_, variables) => handlePreparedFile(variables),
  });

  const exportPaymentsMutation = useExportFile({
    type: 'export_payments',
    addPreparingFile: (name) => setPreparingList((prev) => [...prev, name]),
    onSuccess: (_, variables) => handlePreparedFile(variables),
  });

  const exportReceiptsMutation = useExportFile({
    type: 'export_receipts',
    addPreparingFile: (name) => setPreparingList((prev) => [...prev, name]),
    onSuccess: (_, variables) => handlePreparedFile(variables),
  });

  const loading =
    exportLeadsMutation.isPending ||
    exportAccountsMutation.isPending ||
    exportClassesMutation.isPending ||
    exportContractsMutation.isPending ||
    exportReceiptsMutation.isPending ||
    exportPaymentsMutation.isPending;

  return (
    <>
      {loading && (
        <IconButton color="primary" onClick={handleOpen}>
          <CircularProgress size="20px" />
        </IconButton>
      )}

      <Popover
        open={!!open}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.5,
              ml: 0.75,
              width: 360,
            },
          },
        }}
      >
        <Box sx={{ ...hideScroll.y, maxHeight: '400px' }}>
          <List disablePadding>
            {preparingList.slice(0, 2).map((filename) => (
              <Item key={filename} filename={filename} />
            ))}
          </List>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />
      </Popover>
    </>
  );
}

const Item: FC<{ filename: string }> = ({ filename }) => {
  return (
    <ListItemButton
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
      }}
    >
      <ListItemText
        primary={<Typography variant="subtitle2">Đang chuẩn bị xuất file...</Typography>}
        secondary={
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled',
            }}
          >
            <DescriptionIcon />
            {filename}
          </Typography>
        }
      />
    </ListItemButton>
  );
};
